import { useMemo, memo, useEffect, useRef } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import type { Place, MarkerState, PlaceLayer } from '../types/Place';

interface PlaceMarkerProps {
  place: Place;
  onClick: (id: string) => void;
  isVisible?: boolean;
  isInteractive?: boolean;
  state?: MarkerState;
}

function createGlowingIcon(state: MarkerState = 'default', isInteractive = true, layer: PlaceLayer = 'travels'): L.DivIcon {
  const disabledClass = isInteractive ? '' : 'place-marker-disabled';
  const layerClass = `marker-layer-${layer}`;
  return L.divIcon({
    className: `place-marker ${disabledClass}`.trim(),
    html: `<div class="marker-dot marker-animate-in marker-state-${state} ${layerClass}"></div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
  });
}

export const PlaceMarker = memo(function PlaceMarker({ place, onClick, isVisible = true, isInteractive = true, state = 'default' }: PlaceMarkerProps) {
  const markerRef = useRef<L.Marker | null>(null);

  const handleClick = () => {
    if (isInteractive) {
      onClick(place.id);
    }
  };

  // Memoize icon to prevent Leaflet from recreating DOM elements
  const icon = useMemo(
    () => createGlowingIcon(state, isInteractive, place.layer),
    [state, isInteractive, place.layer]
  );

  // Add keyboard accessibility to marker DOM element
  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    const element = marker.getElement();
    if (!element) return;

    // Set accessibility attributes
    element.setAttribute('role', 'button');
    element.setAttribute('tabindex', isInteractive ? '0' : '-1');
    const viewedStatus = state === 'visited' ? 'Viewed. ' : '';
    const dateInfo = place.date ? `, ${place.date}` : '';
    element.setAttribute(
      'aria-label',
      `${viewedStatus}${place.location}${dateInfo}. ${isInteractive ? 'Press Enter to view details.' : 'Not yet available.'}`
    );

    // Keyboard handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isInteractive) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(place.id);
      }
    };

    element.addEventListener('keydown', handleKeyDown);
    return () => element.removeEventListener('keydown', handleKeyDown);
  }, [place, onClick, isInteractive, state]);

  if (!isVisible) {
    return null;
  }

  return (
    <Marker
      ref={markerRef}
      position={place.coordinates}
      icon={icon}
      zIndexOffset={state === 'highlighted' ? 1000 : 0}
      eventHandlers={{
        click: handleClick,
      }}
    />
  );
});
